import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useWebSocket } from '../WebSocketContext';
import MapEditor from './MapEditor';
import './CreateJourneyModal.css';

const apiUrl = process.env.REACT_APP_API_URL;
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['places', 'marker'];

const goalOptions = [
  'Relaxation', 'Adventure', 'Learning', 'Connection', 'Escape', 'Celebration'
];

const desireOptions = [
  'Beach', 'Mountains', 'City', 'Countryside', 'Culture', 'Food', 'History', 'Nature', 'Outdoors', 'Exploration'
];

const feelingOptions = [
  'Excited', 'Peaceful', 'Energetic', 'Curious', 'Nostalgic', 'Inspired', 'Adventurous', 'Cozy'
];

const seasonOptions = [
  'Spring', 'Summer', 'Fall', 'Winter'
];

// Country and State lists (from AccountSettings.js)
const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin',
  'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
  'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia',
  'Comoros', 'Congo (Congo-Brazzaville)', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea',
  'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
  'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
  'Myanmar (Burma)', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine State', 'Panama', 'Papua New Guinea', 'Paraguay',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Tajikistan', 'Tanzania', 'Thailand',
  'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 
  'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 
  'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 
  'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'N/A'
];

const CreateJourneyModal = ({ isOpen, onClose, onJourneyCreated, initialJourney = null, editPolygonOnly = false }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries
  });
  const { connectionId } = useWebSocket(); // Get connectionId for journey progress recalculation

  const [activeTab, setActiveTab] = useState('auto'); // 'auto', 'mapEditor', or 'manual' - default to auto
  const [step, setStep] = useState('form'); // 'form', 'review', 'waiting'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdJourneyId, setCreatedJourneyId] = useState(null);
  const [isWaitingForWebSocket, setIsWaitingForWebSocket] = useState(false);
  const [map, setMap] = useState(null);
  const [dataLayer, setDataLayer] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [showAdvancedGeoJson, setShowAdvancedGeoJson] = useState(false);
  const [reviewEditMode, setReviewEditMode] = useState(false);
  const [isOptionalInfoExpanded, setIsOptionalInfoExpanded] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    public: false,
    type: 'CUSTOM', // Always CUSTOM
    desires: [],
    feelings: [],
    goals: [],
    recommendedSeason: [],
    overallRating: 0, // Default to 0 for new journeys with no reviews
    scope: {
      level: 'Global',
      countries: [],
      states: []
    },
    selectedCountry: '',
    selectedState: '',
    geojson: null, // Will be FeatureCollection for manual, or description string for auto
    aiDescription: '' // For auto tab
  });

  const [errors, setErrors] = useState({});

  // Convert geospatialBoundaries to FeatureCollection (handles boundingbox format)
  const geospatialToFeatureCollection = (geo) => {
    if (!geo) return null;
    const parsed = typeof geo === 'string' ? JSON.parse(geo) : JSON.parse(JSON.stringify(geo));
    if (parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
      return parsed;
    }
    // Boundingbox format: { locationName: { bounds: { minLon, maxLon, minLat, maxLat } } }
    if (typeof parsed === 'object' && !parsed.type) {
      const features = Object.keys(parsed).map((locationName) => {
        const loc = parsed[locationName];
        if (!loc?.bounds) return null;
        const { minLon, maxLon, minLat, maxLat } = loc.bounds;
        return {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [minLon, minLat], [maxLon, minLat], [maxLon, maxLat], [minLon, maxLat], [minLon, minLat]
            ]]
          },
          properties: loc.properties || { name: locationName }
        };
      }).filter(Boolean);
      return features.length > 0 ? { type: 'FeatureCollection', features } : null;
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      if (initialJourney) {
        // If initialJourney is provided, set up for review mode
        setCreatedJourneyId(initialJourney.journeyID || initialJourney.journeyId);
        
        // Convert geospatialBoundaries to FeatureCollection (handles both GeoJSON and boundingbox)
        const geojsonData = geospatialToFeatureCollection(initialJourney.geospatialBoundaries);
        
        console.log('Setting up review mode with journey:', {
          journeyID: initialJourney.journeyID || initialJourney.journeyId,
          hasGeojson: !!geojsonData,
          featureCount: geojsonData?.features?.length || 0,
          geojsonType: geojsonData?.type
        });
        
        setFormData({
          title: initialJourney.title || '',
          description: initialJourney.description || '',
          public: initialJourney.public !== undefined 
            ? (initialJourney.public === true || initialJourney.public === 'true' ? true : false)
            : false,
          type: initialJourney.type || 'CUSTOM',
          desires: initialJourney.desires || [],
          feelings: initialJourney.feelings || [],
          goals: initialJourney.goals || [],
          recommendedSeason: initialJourney.recommendedSeason || [],
          overallRating: initialJourney.overallRating || 0,
          scope: initialJourney.scope || {
            level: 'Global',
            countries: [],
            states: []
          },
          selectedCountry: initialJourney.scope?.countries?.[0] || '',
          selectedState: initialJourney.scope?.states?.[0] || '',
          geojson: geojsonData,
          aiDescription: initialJourney.aiDescription || ''
        });
        setStep('review'); // Go directly to review step
        setActiveTab(initialJourney.geospatialBoundaries ? 'mapEditor' : 'auto');
        setIsSubmitting(false); // Reset submitting state when opening with initialJourney
        setIsOptionalInfoExpanded(true); // Show optional info expanded by default for review
      } else {
        // Reset form when modal opens for new journey
        setFormData({
          title: '',
          description: '',
          public: false,
          type: 'CUSTOM', // Always CUSTOM
          desires: [],
          feelings: [],
          goals: [],
          recommendedSeason: [],
          overallRating: 0, // Default to 0 for new journeys with no reviews
          scope: {
            level: 'Global',
            countries: [],
            states: []
          },
          selectedCountry: '',
          selectedState: '',
          geojson: null,
          aiDescription: ''
        });
        setActiveTab('auto'); // Default to AI Generate tab
        setStep('form');
        setCreatedJourneyId(null);
        setIsSubmitting(false);
        setIsWaitingForWebSocket(false);
        setShowAdvancedGeoJson(false);
        setReviewEditMode(false);
      }
      setErrors({});
    } else {
      // Cleanup when modal closes - clear any pending timeouts and event listeners
      if (createdJourneyId && window[`journeyTimeout_${createdJourneyId}`]) {
        clearTimeout(window[`journeyTimeout_${createdJourneyId}`]);
        delete window[`journeyTimeout_${createdJourneyId}`];
      }
    }
  }, [isOpen, createdJourneyId, initialJourney]);

  // Normalize GeoJSON coordinates to fix common formatting issues
  const normalizeGeoJSON = (geoJson) => {
    if (!geoJson || !geoJson.features) return geoJson;

    const normalized = JSON.parse(JSON.stringify(geoJson)); // Deep clone

    normalized.features = normalized.features.map((feature, idx) => {
      if (!feature.geometry || !feature.geometry.coordinates) return feature;

      // Preserve properties (name, locationName, etc.) - ensure we don't strip them
      const properties = feature.properties && typeof feature.properties === 'object'
        ? { ...feature.properties }
        : {};

      const geometry = feature.geometry;
      const coords = geometry.coordinates;

      // Fix Polygon coordinates - they should be [LinearRing] where LinearRing is [[lng, lat], ...]
      if (geometry.type === 'Polygon') {
        // Check if coordinates are missing the outer array
        if (coords.length > 0 && Array.isArray(coords[0]) && coords[0].length > 0) {
          // Check if first element is a number (missing outer array) or an array of numbers (correct)
          const firstCoord = coords[0];
          if (typeof firstCoord[0] === 'number') {
            // Missing outer array - wrap it
            geometry.coordinates = [coords];
          }
        }
      }

      // Fix MultiPolygon coordinates - they should be [[LinearRing], ...]
      if (geometry.type === 'MultiPolygon') {
        if (coords.length > 0 && Array.isArray(coords[0])) {
          const firstRing = coords[0];
          if (firstRing.length > 0 && typeof firstRing[0] === 'number') {
            // Missing outer array for first polygon
            geometry.coordinates = [coords];
          } else if (firstRing.length > 0 && Array.isArray(firstRing[0]) && typeof firstRing[0][0] === 'number') {
            // Missing outer array for MultiPolygon
            geometry.coordinates = [coords];
          }
        }
      }

      return { ...feature, properties };
    });

    return normalized;
  };

  // Load GeoJSON on map when reviewing
  useEffect(() => {
    if (step === 'review' && map && formData.geojson && window.google) {
      // Clear existing data layer
      if (dataLayer) {
        dataLayer.setMap(null);
      }

      try {
        // Validate GeoJSON before processing
        if (!formData.geojson || 
            typeof formData.geojson !== 'object' || 
            formData.geojson.type !== 'FeatureCollection' || 
            !Array.isArray(formData.geojson.features)) {
          console.error('Invalid GeoJSON format:', formData.geojson);
          alert('Error: Invalid GeoJSON format. The journey GeoJSON is not ready yet or is malformed.');
          return;
        }

        // Normalize GeoJSON to fix common coordinate structure issues
        const normalizedGeoJson = normalizeGeoJSON(formData.geojson);

        // Validate normalized GeoJSON
        if (!normalizedGeoJson || 
            normalizedGeoJson.type !== 'FeatureCollection' || 
            !Array.isArray(normalizedGeoJson.features)) {
          console.error('Invalid normalized GeoJSON format:', normalizedGeoJson);
          alert('Error: GeoJSON normalization failed. Please try creating the journey again.');
          return;
        }

        // Create new data layer
        const newDataLayer = new window.google.maps.Data();
        
        // Set styling to match journey overlay styling (thin borders, proper opacity)
        newDataLayer.setStyle((feature) => {
          return {
            fillColor: '#4285F4', // Default blue color for review
            fillOpacity: 0.3, // 30% opacity (matching non-completed journey styling)
            strokeColor: '#4285F4',
            strokeOpacity: 0.5, // 50% opacity for borders (matching journey styling)
            strokeWeight: 1 // Thin border (matching journey styling)
          };
        });
        
        newDataLayer.addGeoJson(normalizedGeoJson);
        newDataLayer.setMap(map);

        // Fit bounds to GeoJSON
        const bounds = new window.google.maps.LatLngBounds();
        let hasBounds = false;

        newDataLayer.forEach((feature) => {
          const geometry = feature.getGeometry();
          if (geometry) {
            geometry.forEachLatLng((latLng) => {
              bounds.extend(latLng);
              hasBounds = true;
            });
          }
        });

        if (hasBounds && !bounds.isEmpty()) {
          // Store bounds for map restriction
          setMapBounds(bounds);
          
          // Fit bounds with padding
          map.fitBounds(bounds, { padding: 50 });
          
          // Set minimum zoom to prevent showing multiple world maps
          // Calculate appropriate min zoom based on bounds
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latDiff = ne.lat() - sw.lat();
          const lngDiff = ne.lng() - sw.lng();
          
          // Set min zoom based on extent - larger areas get lower min zoom
          let minZoom = 2; // Default to prevent multiple world maps
          if (latDiff < 1 && lngDiff < 1) {
            minZoom = 8; // Very small area
          } else if (latDiff < 5 && lngDiff < 5) {
            minZoom = 5; // Small area
          } else if (latDiff < 20 && lngDiff < 20) {
            minZoom = 3; // Medium area
          } else {
            minZoom = 2; // Large area (continent/global) - prevent multiple world maps
          }
          
          // Expand bounds slightly for restriction (to allow some padding around features)
          const expandedBounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(sw.lat() - latDiff * 0.1, sw.lng() - lngDiff * 0.1),
            new window.google.maps.LatLng(ne.lat() + latDiff * 0.1, ne.lng() + lngDiff * 0.1)
          );
          
          // Restrict map to bounds and set min zoom to prevent multiple world maps
          map.setOptions({
            restriction: {
              latLngBounds: expandedBounds,
              strictBounds: false // Allow some padding outside bounds
            },
            minZoom: minZoom,
            maxZoom: 20
          });
        }

        setDataLayer(newDataLayer);

        return () => {
          if (newDataLayer) {
            newDataLayer.setMap(null);
          }
        };
      } catch (error) {
        console.error('Error loading GeoJSON on map:', error);
        // Show user-friendly error
        alert(`Error displaying GeoJSON on map: ${error.message}. Please check your coordinate structure.`);
      }
    }
  }, [step, map, formData.geojson]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (activeTab === 'mapEditor' || activeTab === 'manual') {
      if (!formData.geojson) {
        newErrors.geojson = 'GeoJSON is required. Please draw at least one polygon on the map.';
      } else if (!formData.geojson.features || formData.geojson.features.length === 0) {
        newErrors.geojson = 'At least one polygon is required. Please draw a polygon on the map.';
      } else {
        // Validate GeoJSON structure and size
        try {
          const geoJsonString = typeof formData.geojson === 'string' 
            ? formData.geojson 
            : JSON.stringify(formData.geojson);
          
          // Limit to 200KB (API Gateway limit is typically 10MB, but DynamoDB item limit is 400KB)
          // We'll use 200KB to be safe and leave room for other fields
          const maxSizeBytes = 200 * 1024; // 200KB
          const sizeBytes = new Blob([geoJsonString]).size;
          
          if (sizeBytes > maxSizeBytes) {
            newErrors.geojson = `GeoJSON is too large (${(sizeBytes / 1024).toFixed(1)}KB). Maximum size is 200KB.`;
          } else {
            const parsed = typeof formData.geojson === 'string' 
              ? JSON.parse(formData.geojson) 
              : formData.geojson;
            
            if (!parsed.type || parsed.type !== 'FeatureCollection') {
              newErrors.geojson = 'GeoJSON must be a FeatureCollection';
            } else if (!parsed.features || !Array.isArray(parsed.features)) {
              newErrors.geojson = 'GeoJSON must have a features array';
            } else {
              // Validate coordinate structure for each feature
              for (let i = 0; i < parsed.features.length; i++) {
                const feature = parsed.features[i];
                if (!feature.geometry || !feature.geometry.coordinates) {
                  newErrors.geojson = `Feature ${i + 1} is missing geometry or coordinates`;
                  break;
                }

                const geometry = feature.geometry;
                const coords = geometry.coordinates;

                // Validate Polygon coordinates structure
                if (geometry.type === 'Polygon') {
                  if (!Array.isArray(coords) || coords.length === 0) {
                    newErrors.geojson = `Feature ${i + 1}: Polygon coordinates must be a non-empty array`;
                    break;
                  }
                  // Check if coordinates need fixing (missing outer array)
                  if (coords.length > 0 && Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
                    // This is fixable - coordinates are [lng, lat] instead of [[lng, lat], ...]
                    // We'll auto-fix this, but warn the user
                    console.warn(`Feature ${i + 1}: Polygon coordinates structure will be auto-corrected`);
                  }
                }

                // Validate MultiPolygon coordinates structure
                if (geometry.type === 'MultiPolygon') {
                  if (!Array.isArray(coords) || coords.length === 0) {
                    newErrors.geojson = `Feature ${i + 1}: MultiPolygon coordinates must be a non-empty array`;
                    break;
                  }
                }
              }
            }
          }
        } catch (e) {
          newErrors.geojson = `Invalid GeoJSON format: ${e.message}`;
        }
      }
    } else {
      if (!formData.aiDescription.trim()) {
        newErrors.aiDescription = 'Description is required for AI generation';
      }
    }

    // Validate scope level selections
    if (formData.scope.level === 'Country' && !formData.selectedCountry) {
      newErrors.selectedCountry = 'Please select a country';
    }
    if (formData.scope.level === 'State' && !formData.selectedState) {
      newErrors.selectedState = 'Please select a state';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleScopeChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        scope: {
          ...prev.scope,
          [field]: value
        }
      };
      
      // Reset country/state selections when scope level changes
      if (field === 'level') {
        updated.selectedCountry = '';
        updated.selectedState = '';
        updated.scope.countries = [];
        updated.scope.states = [];
      }
      
      return updated;
    });
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({
      ...prev,
      selectedCountry: country,
      scope: {
        ...prev.scope,
        countries: country ? [country] : []
      }
    }));
  };

  const handleStateChange = (state) => {
    setFormData(prev => ({
      ...prev,
      selectedState: state,
      scope: {
        ...prev.scope,
        states: state ? [state] : []
      }
    }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const currentValues = prev[field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [field]: newValues
      };
    });
  };

  const handleGeoJSONChange = (value) => {
    // Check size limit first
    const maxSizeBytes = 200 * 1024; // 200KB
    const sizeBytes = new Blob([value]).size;
    
    if (sizeBytes > maxSizeBytes) {
      setFormData(prev => ({ ...prev, geojson: null }));
      setErrors(prev => ({ 
        ...prev, 
        geojson: `GeoJSON is too large (${(sizeBytes / 1024).toFixed(1)}KB). Maximum size is 200KB.` 
      }));
      return;
    }

    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      setFormData(prev => ({ ...prev, geojson: parsed }));
      if (errors.geojson) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.geojson;
          return newErrors;
        });
      }
    } catch (e) {
      setFormData(prev => ({ ...prev, geojson: null }));
      setErrors(prev => ({ ...prev, geojson: 'Invalid JSON format' }));
    }
  };

  const createJourney = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Normalize GeoJSON before sending (fix coordinate structure issues)
      let normalizedGeoJson = formData.geojson;
      if ((activeTab === 'mapEditor' || activeTab === 'manual') && formData.geojson) {
        normalizedGeoJson = normalizeGeoJSON(formData.geojson);
      }

      // Check if we're updating an existing journey
      if (createdJourneyId) {
        // Fetch the current journey to get the full object
        // Use dataType from initialJourney if available, otherwise default to geojson
        const dataType = initialJourney?.dataType || 'geojson';
        const journeyResponse = await axios.get(
          `${apiUrl}/journeys/${createdJourneyId}?dataType=${dataType}`,
          { headers }
        );
        
        const currentJourney = journeyResponse.data;
        
        // Prepare updated payload with form data
        const updatedPayload = {
          ...currentJourney, // Keep all existing fields
          title: formData.title,
          description: formData.description,
          public: formData.public,
          type: formData.type,
          dataType: 'geoJSON',
          desires: formData.desires.length > 0 ? formData.desires : undefined,
          feelings: formData.feelings.length > 0 ? formData.feelings : undefined,
          goals: formData.goals.length > 0 ? formData.goals : undefined,
          recommendedSeason: formData.recommendedSeason.length > 0 ? formData.recommendedSeason : undefined,
          overallRating: formData.overallRating,
          scope: {
            level: formData.scope.level,
            countries: formData.scope.countries.length > 0 ? formData.scope.countries : (formData.scope.level === 'Global' ? ['N/A'] : []),
            states: formData.scope.states.length > 0 ? formData.scope.states : (formData.scope.level === 'Global' ? ['N/A'] : [])
          },
          isActive: false // Keep as inactive until user accepts
        };

        // Update GeoJSON if mapEditor or manual tab
        if ((activeTab === 'mapEditor' || activeTab === 'manual') && normalizedGeoJson) {
          updatedPayload.geospatialBoundaries = normalizedGeoJson;
        }

        // Add AI description if auto tab
        if (activeTab === 'auto') {
          updatedPayload.aiDescription = formData.aiDescription;
        }
        
        // Add AI description for mapEditor if it exists (from previous AI generation)
        if (activeTab === 'mapEditor' && formData.aiDescription) {
          updatedPayload.aiDescription = formData.aiDescription;
        }

        // Remove undefined fields
        Object.keys(updatedPayload).forEach(key => {
          if (updatedPayload[key] === undefined) {
            delete updatedPayload[key];
          }
        });

        // Update existing journey with PUT
        await axios.put(
          `${apiUrl}/journeys/${createdJourneyId}`,
          updatedPayload,
          { headers }
        );

        // If mapEditor (polygon edit from map popup), refresh and close immediately
        if (activeTab === 'mapEditor') {
          setIsSubmitting(false);
          if (onJourneyCreated) onJourneyCreated();
          onClose();
          return;
        }

        // If manual, set geojson for review. If auto, we need to fetch the updated journey
        if (activeTab === 'manual') {
          setStep('review');
        } else {
          // For auto, fetch the updated journey to get the generated GeoJSON
          // Wait a bit for AI processing, then poll for the journey
          let attempts = 0;
          const maxAttempts = 10;
          while (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
            try {
              // For newly created journeys, default to geojson, but check if journey has dataType
              const dataType = initialJourney?.dataType || 'geojson';
              const updatedJourneyResponse = await axios.get(
                `${apiUrl}/journeys/${createdJourneyId}?dataType=${dataType}`,
                { headers }
              );
              if (updatedJourneyResponse.data.geospatialBoundaries) {
                setFormData(prev => ({ ...prev, geojson: updatedJourneyResponse.data.geospatialBoundaries }));
                setStep('review');
                break;
              }
            } catch (error) {
              console.error('Error fetching journey:', error);
            }
            attempts++;
          }
          if (attempts >= maxAttempts) {
            alert('Journey updated but GeoJSON is still being generated. Please check back later.');
            if (onJourneyCreated) {
              onJourneyCreated();
            }
            onClose();
          }
        }
      } else {
        // Create new journey with POST
        const payload = {
          title: formData.title,
          description: formData.description,
          public: formData.public,
          type: formData.type,
          dataType: 'geoJSON',
          desires: formData.desires.length > 0 ? formData.desires : undefined,
          feelings: formData.feelings.length > 0 ? formData.feelings : undefined,
          goals: formData.goals.length > 0 ? formData.goals : undefined,
          recommendedSeason: formData.recommendedSeason.length > 0 ? formData.recommendedSeason : undefined,
          overallRating: formData.overallRating,
          scope: {
            level: formData.scope.level,
            countries: formData.scope.countries.length > 0 ? formData.scope.countries : (formData.scope.level === 'Global' ? ['N/A'] : []),
            states: formData.scope.states.length > 0 ? formData.scope.states : (formData.scope.level === 'Global' ? ['N/A'] : [])
          },
          geospatialBoundaries: (activeTab === 'mapEditor' || activeTab === 'manual') ? normalizedGeoJson : undefined,
          isActive: false // Create as inactive
        };

        // Remove undefined fields
        Object.keys(payload).forEach(key => {
          if (payload[key] === undefined) {
            delete payload[key];
          }
        });

        // Add AI description if auto tab
        if (activeTab === 'auto') {
          payload.aiDescription = formData.aiDescription;
        }

        const reqType = (activeTab === 'mapEditor' || activeTab === 'manual') ? 'manual' : 'auto';
        const response = await axios.post(
          `${apiUrl}/journeys?dataType=geoJSON&reqType=${reqType}`,
          payload,
          { headers }
        );

        const journeyId = response.data.journeyID || response.data.journeyId;
        setCreatedJourneyId(journeyId);
        
        // Handle 200 (immediate) vs 201 (async processing) responses
        if (response.status === 201) {
          // Journey is being processed asynchronously
          setIsSubmitting(false);
          
          // For manual/mapEditor routes, add journey immediately even if 201
          // (similar to quick add behavior)
          if (activeTab === 'mapEditor' || activeTab === 'manual') {
            try {
              // Fetch journey details to pass to addJourneyToUser
              const dataType = 'geojson';
              let journeyData = null;
              try {
                const journeyResponse = await axios.get(
                  `${apiUrl}/journeys/${journeyId}?dataType=${dataType}`,
                  { headers }
                );
                journeyData = journeyResponse.data;
              } catch (fetchError) {
                console.warn('Could not fetch journey details, proceeding without journey object:', fetchError);
              }
              
              // Add journey to user's explorer board
              await addJourneyToUser(journeyId, journeyData);
              console.log('Journey added to explorer board after 201 response (manual route)');
              
              // Refresh the explorer board
              if (onJourneyCreated) {
                onJourneyCreated();
              }
            } catch (addError) {
              console.error('Error adding journey to explorer board:', addError);
              // Don't fail the creation if adding to explorer board fails
            }
            
            // For manual routes, go to review step
            setStep('review');
          } else {
            // For auto routes, close modal and redirect back
            // The WebSocket notification will be handled by MainApp
            // Store journey ID in window for MainApp to track and show notification
            if (!window.pendingJourneys) {
              window.pendingJourneys = new Set();
            }
            window.pendingJourneys.add(journeyId);
            
            // Close modal and return to previous page
            if (onJourneyCreated) {
              onJourneyCreated();
            }
            onClose();
          }
          
        } else if (response.status === 200) {
          // Journey is ready immediately
          setIsSubmitting(false); // Safe to set submitting to false for 200 responses
          
          // Add journey to user's explorer board and journey list
          try {
            // Fetch journey details to pass to addJourneyToUser (similar to quick add)
            const dataType = 'geojson'; // Newly created journeys are typically geojson
            let journeyData = null;
            try {
              const journeyResponse = await axios.get(
                `${apiUrl}/journeys/${journeyId}?dataType=${dataType}`,
                { headers }
              );
              journeyData = journeyResponse.data;
            } catch (fetchError) {
              console.warn('Could not fetch journey details, proceeding without journey object:', fetchError);
            }
            
            // Add journey to user's explorer board
            await addJourneyToUser(journeyId, journeyData);
            console.log('Journey added to explorer board after 200 response');
            
            // Refresh the explorer board by calling onJourneyCreated
            if (onJourneyCreated) {
              onJourneyCreated();
            }
          } catch (addError) {
            console.error('Error adding journey to explorer board:', addError);
            // Don't fail the creation if adding to explorer board fails
            // Just log the error and continue
          }
          
          // If mapEditor or manual, set geojson for review. If auto, we need to fetch the created journey
          if (activeTab === 'mapEditor' || activeTab === 'manual') {
            setStep('review');
          } else {
            // For auto, fetch the created journey to get the generated GeoJSON
            // Wait a bit for AI processing, then poll for the journey
            let attempts = 0;
            const maxAttempts = 10;
            while (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
              try {
                // For newly created journeys, default to geojson
                const dataType = 'geojson'; // Newly created journeys are typically geojson
                const journeyResponse = await axios.get(
                  `${apiUrl}/journeys/${journeyId}?dataType=${dataType}`,
                  { headers }
                );
                const geoJson = journeyResponse.data.geospatialBoundaries;
                // Validate GeoJSON before setting it
                if (geoJson && 
                    typeof geoJson === 'object' && 
                    geoJson.type === 'FeatureCollection' && 
                    Array.isArray(geoJson.features)) {
                  setFormData(prev => ({ ...prev, geojson: geoJson }));
                  setIsSubmitting(false); // Set submitting to false when ready
                  setStep('review');
                  break;
                }
              } catch (error) {
                console.error('Error fetching journey:', error);
              }
              attempts++;
            }
            if (attempts >= maxAttempts) {
              setIsSubmitting(false);
              alert('Journey created but GeoJSON is still being generated. Please check back later.');
              if (onJourneyCreated) {
                onJourneyCreated();
              }
              onClose();
            }
          }
        }
      }
    } catch (error) {
      console.error('Error creating/updating journey:', error);
      setIsSubmitting(false);
      alert(`Failed to ${createdJourneyId ? 'update' : 'create'} journey: ${error.response?.data?.message || error.message}`);
    }
    // Note: Don't set isSubmitting(false) in finally block for 201 responses
    // It will be set to false when websocket arrives or timeout completes
    // For 200 responses, isSubmitting is already handled in the response handling code
  };

  const getUserFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  const generateRandomJourneyColor = () => {
    const colors = [
      { r: 255, g: 0, b: 0 }, { r: 0, g: 255, b: 0 }, { r: 0, g: 0, b: 255 },
      { r: 255, g: 255, b: 0 }, { r: 255, g: 0, b: 255 }, { r: 0, g: 255, b: 255 },
      { r: 255, g: 165, b: 0 }, { r: 128, g: 0, b: 128 }, { r: 255, g: 192, b: 203 }
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const addJourneyToUser = async (journeyID, journey = null) => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        throw new Error('Failed to retrieve user from JWT token.');
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      // Fetch current user data to get existing journeys
      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      const getUserResponse = await axios.get(url, { headers });
      const currentUserData = getUserResponse.data[0];
      const existingJourneys = currentUserData?.journeys || [];

      // Check if journey already exists
      const existingJourneyIndex = existingJourneys.findIndex(
        j => j.elementID === journeyID
      );

      // Check journey limit (10 active journeys max - completed journeys don't count)
      const MAX_ACTIVE_JOURNEYS = 10;
      const activeJourneys = existingJourneys.filter(j => !j.isComplete);
      if (existingJourneyIndex < 0 && activeJourneys.length >= MAX_ACTIVE_JOURNEYS) {
        throw new Error(`Active journey limit reached. You can only have up to ${MAX_ACTIVE_JOURNEYS} active journeys. Complete existing journeys to add more.`);
      }

      // Check current counts for displayJourney and isActive
      const currentlyDisplayed = existingJourneys.filter(j => j.displayJourney === true).length;
      const currentlyActive = existingJourneys.filter(j => j.isActive === true).length;
      
      // Determine if new journey should be displayed/active (max 3 each)
      const shouldDisplay = currentlyDisplayed < 3;
      const shouldBeActive = currentlyActive < 3;
      
      const journeyColor = generateRandomJourneyColor();
      const newJourney = {
        elementID: journeyID,
        journeyColor: journeyColor,
        displayItemsMode: "Both",
        overrides: [],
        isActive: shouldBeActive,
        manualPercentComplete: 0.0,
        displayJourney: shouldDisplay,
        isComplete: false,
        isFavorite: false
      };

      let updatedJourneys;
      if (existingJourneyIndex >= 0) {
        // Update existing journey
        updatedJourneys = [...existingJourneys];
        updatedJourneys[existingJourneyIndex] = {
          ...newJourney,
          displayJourney: shouldDisplay,
          isActive: shouldBeActive
        };
      } else {
        // Add new journey
        updatedJourneys = [...existingJourneys, newJourney];
      }

      // Create PATCH payload with updated journeys array
      const patchData = {
        journeys: updatedJourneys
      };

      await axios.patch(url, patchData, { headers });
      
      // Trigger journey progress recalculation after adding journey
      // This will update journey progress via WebSocket
      try {
        // Get connectionId from WebSocket context
        const wsConnectionId = connectionId || '';
        
        // Build POST body with all journeys including the newly added one
        // We need ownerID for each journey - use journey parameter if available, otherwise fetch
        const journeysWithOwnerID = await Promise.all(
          updatedJourneys.map(async (j) => {
            try {
              // If this is the newly added journey and we have journey data, use it
              if (j.elementID === journeyID && journey && journey.ownerID) {
                return {
                  journeyID: j.elementID,
                  ownerID: journey.ownerID
                };
              }
              
              // Otherwise, fetch journey details to get ownerID
              // Use dataType from journey parameter if available
              const journeyForDataType = (j.elementID === journeyID && journey) ? journey : null;
              const dataType = journeyForDataType?.dataType ? `?dataType=${journeyForDataType.dataType}` : '';
              const journeyResponse = await axios.get(`${apiUrl}/journeys/${j.elementID}${dataType}`, { headers });
              const journeyData = journeyResponse.data;
              return {
                journeyID: j.elementID,
                ownerID: journeyData.ownerID || 'System'
              };
            } catch (error) {
              // If fetch fails, use System as default
              console.warn(`Could not fetch ownerID for journey ${j.elementID}, defaulting to System:`, error);
              return {
                journeyID: j.elementID,
                ownerID: 'System'
              };
            }
          })
        );
        
        const postBody = {
          journeys: journeysWithOwnerID.filter(j => j.journeyID), // Only include journeys with journeyID
          clientId: wsConnectionId
        };
        
        // Trigger recalculation asynchronously (non-blocking)
        axios.post(
          `${apiUrl}/users/${encodeURIComponent(user)}/journey-progress?sendWebSocket=true`,
          postBody,
          { headers }
        ).then(response => {
          if (response.status === 201) {
            console.log('Journey progress recalculation triggered after adding journey (201)', response.data);
          } else {
            console.log('Journey progress recalculation triggered (status:', response.status, ')', response.data);
          }
        }).catch(progressError => {
          // Don't fail journey addition if progress recalculation fails
          console.warn('Failed to trigger journey progress recalculation after adding journey:', progressError);
        });
      } catch (progressError) {
        // Don't fail journey addition if progress recalculation setup fails
        console.warn('Error setting up journey progress recalculation:', progressError);
      }
      
      return true;
    } catch (error) {
      console.error('Error adding journey to user:', error);
      throw error;
    }
  };

  const handleAccept = async () => {
    if (!createdJourneyId) return;

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Normalize GeoJSON if it exists in formData (may have been edited)
      let normalizedGeoJson = formData.geojson;
      if (formData.geojson) {
        normalizedGeoJson = normalizeGeoJSON(formData.geojson);
      }

      let currentJourney;

      if (editPolygonOnly && initialJourney) {
        // Edit from map/detail page: use initialJourney as base, no GET needed
        // Same pattern as createJourney PUT - update entire journey from formData
        currentJourney = { ...initialJourney };
      } else {
        // WebSocket review: fetch current journey to get full object
        const dataType = initialJourney?.dataType || 'geojson';
        const journeyResponse = await axios.get(
          `${apiUrl}/journeys/${createdJourneyId}?dataType=${dataType}`,
          { headers }
        );
        currentJourney = journeyResponse.data;
      }

      // Build full journey update - same structure as createJourney PUT
      const updatedJourney = {
        ...currentJourney,
        journeyID: createdJourneyId,
        title: formData.title,
        description: formData.description,
        public: formData.public,
        type: formData.type || currentJourney.type,
        desires: formData.desires?.length > 0 ? formData.desires : (currentJourney.desires || []),
        feelings: formData.feelings?.length > 0 ? formData.feelings : (currentJourney.feelings || []),
        goals: formData.goals?.length > 0 ? formData.goals : (currentJourney.goals || []),
        recommendedSeason: formData.recommendedSeason?.length > 0 ? formData.recommendedSeason : (currentJourney.recommendedSeason || []),
        overallRating: formData.overallRating ?? currentJourney.overallRating,
        scope: formData.scope || currentJourney.scope,
        isActive: editPolygonOnly ? (currentJourney.isActive ?? false) : true,
        ...(normalizedGeoJson && { geospatialBoundaries: normalizedGeoJson })
      };

      // Remove undefined fields
      Object.keys(updatedJourney).forEach(key => {
        if (updatedJourney[key] === undefined) {
          delete updatedJourney[key];
        }
      });

      // PUT to update entire journey (same as createJourney edit flow)
      await axios.put(
        `${apiUrl}/journeys/${createdJourneyId}`,
        updatedJourney,
        { headers }
      );

      // Add journey to user's explorer board (skip when editPolygonOnly - already on board)
      if (!editPolygonOnly) {
        try {
          await addJourneyToUser(createdJourneyId, currentJourney);
        } catch (addError) {
          console.error('Error adding journey to explorer board:', addError);
          alert(`Journey activated but failed to add to explorer board: ${addError.message}`);
        }
      }

      if (onJourneyCreated) {
        onJourneyCreated();
      }

      onClose();
    } catch (error) {
      console.error('Error saving journey:', error);
      alert(`Failed to ${editPolygonOnly ? 'save' : 'activate'} journey: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCancel = async () => {
    // When editing polygon from map/detail page, just close - do not delete
    if (editPolygonOnly) {
      onClose();
      return;
    }
    if (!createdJourneyId) {
      onClose();
      return;
    }

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      // Delete the inactive journey (websocket review flow only)
      await axios.delete(`${apiUrl}/journeys/${createdJourneyId}`, { headers });

      onClose();
    } catch (error) {
      console.error('Error deleting journey:', error);
      alert(`Failed to delete journey: ${error.response?.data?.message || error.message}`);
      onClose();
    }
  };

  const handleModify = () => {
    setStep('form');
  };

  if (!isOpen) return null;

  return (
    <div className="create-journey-modal-overlay">
      <div className="create-journey-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{step === 'form' ? 'Create New Journey' : step === 'waiting' ? 'Creating Journey' : 'Review Journey'}</h2>
          <button className="modal-close-btn" onClick={handleCancel}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {step === 'waiting' ? (
          <div className="modal-content waiting-content">
            <div className="waiting-indicator">
              <div className="spinner"></div>
              <h3>Generating Journey GeoJSON</h3>
              <p>Your journey is being processed. This may take a few moments...</p>
              <p className="waiting-details">Journey ID: {createdJourneyId}</p>
            </div>
          </div>
        ) : step === 'form' ? (
          <div className="modal-content">
            {/* Tabs */}
            <div className="create-journey-tabs">
              <button
                className={`tab-button ${activeTab === 'auto' ? 'active' : ''}`}
                onClick={() => setActiveTab('auto')}
              >
                <i className="fas fa-magic"></i>
                <span>AI Enabled</span>
              </button>
              <button
                className={`tab-button ${activeTab === 'mapEditor' ? 'active' : ''}`}
                onClick={() => setActiveTab('mapEditor')}
              >
                <i className="fas fa-map"></i>
                <span>Map Editor</span>
              </button>
            </div>

            <div className="form-content">
              {/* Required Fields */}
              <div className="form-section">
                <h3>Required Information</h3>
                
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter journey title"
                    className={errors.title ? 'error' : ''}
                  />
                  {errors.title && <span className="error-message">{errors.title}</span>}
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter journey description"
                    rows={4}
                    className={errors.description ? 'error' : ''}
                  />
                  {errors.description && <span className="error-message">{errors.description}</span>}
                </div>

                <div className="form-group">
                  <label>Visibility *</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="public"
                        checked={formData.public === true}
                        onChange={() => handleInputChange('public', true)}
                      />
                      <span>Public</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="public"
                        checked={formData.public === false}
                        onChange={() => handleInputChange('public', false)}
                      />
                      <span>Private</span>
                    </label>
                  </div>
                </div>

                {/* Map Editor Tab: Visual Editing */}
                {activeTab === 'mapEditor' && (
                  <div className="form-group">
                    <label>Draw Boundaries on Map *</label>
                    <MapEditor
                      initialGeoJson={formData.geojson}
                      onGeoJsonChange={(geoJson) => {
                        setFormData(prev => ({ ...prev, geojson: geoJson }));
                        // Clear error if GeoJSON is valid
                        if (errors.geojson) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.geojson;
                            return newErrors;
                          });
                        }
                      }}
                      height="500px"
                      showAdvanced={true}
                      onAdvancedToggle={() => setShowAdvancedGeoJson(!showAdvancedGeoJson)}
                    />
                    {errors.geojson && <span className="error-message">{errors.geojson}</span>}
                    
                    {/* Advanced: Manual GeoJSON Editor */}
                    {showAdvancedGeoJson && (
                      <div className="form-group" style={{ marginTop: '20px' }}>
                        <label>Advanced: Edit GeoJSON Manually</label>
                        <div className="geojson-input-wrapper">
                          <textarea
                            value={typeof formData.geojson === 'string' 
                              ? formData.geojson 
                              : formData.geojson 
                                ? JSON.stringify(formData.geojson, null, 2)
                                : ''}
                            onChange={(e) => handleGeoJSONChange(e.target.value)}
                            placeholder='{"type": "FeatureCollection", "features": [...]}'
                            rows={10}
                            className={errors.geojson ? 'error' : ''}
                            maxLength={250000}
                          />
                          <div className="input-footer">
                            {formData.geojson ? (
                              <span className={`size-indicator ${(() => {
                                const geoJsonString = typeof formData.geojson === 'string' 
                                  ? formData.geojson 
                                  : JSON.stringify(formData.geojson);
                                const sizeBytes = new Blob([geoJsonString]).size;
                                return sizeBytes > 200 * 1024 ? 'error' : '';
                              })()}`}>
                                {(() => {
                                  const geoJsonString = typeof formData.geojson === 'string' 
                                    ? formData.geojson 
                                    : JSON.stringify(formData.geojson);
                                  return (new Blob([geoJsonString]).size / 1024).toFixed(1);
                                })()}KB / 200KB
                              </span>
                            ) : (
                              <span className="size-indicator">Max 200KB</span>
                            )}
                          </div>
                        </div>
                        {errors.geojson && <span className="error-message">{errors.geojson}</span>}
                      </div>
                    )}
                  </div>
                )}

                {/* Auto Tab: AI Description */}
                {activeTab === 'auto' && (
                  <div className="form-group">
                    <label>Describe the journey you want to create *</label>
                    <textarea
                      value={formData.aiDescription}
                      onChange={(e) => handleInputChange('aiDescription', e.target.value)}
                      placeholder="E.g., Create a journey for all national parks in Colorado with hiking trails..."
                      rows={6}
                      className={errors.aiDescription ? 'error' : ''}
                    />
                    {errors.aiDescription && <span className="error-message">{errors.aiDescription}</span>}
                  </div>
                )}
              </div>

              {/* Optional Fields */}
              <div className="form-section">
                <h3>Optional Information</h3>

                <div className="form-group">
                  <label>Desires</label>
                  <div className="checkbox-group">
                    {desireOptions.map(desire => (
                      <label key={desire} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.desires.includes(desire)}
                          onChange={() => handleMultiSelect('desires', desire)}
                        />
                        <span>{desire}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Feelings</label>
                  <div className="checkbox-group">
                    {feelingOptions.map(feeling => (
                      <label key={feeling} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.feelings.includes(feeling)}
                          onChange={() => handleMultiSelect('feelings', feeling)}
                        />
                        <span>{feeling}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Goals</label>
                  <div className="checkbox-group">
                    {goalOptions.map(goal => (
                      <label key={goal} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.goals.includes(goal)}
                          onChange={() => handleMultiSelect('goals', goal)}
                        />
                        <span>{goal}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Recommended Season</label>
                  <div className="checkbox-group">
                    {seasonOptions.map(season => (
                      <label key={season} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.recommendedSeason.includes(season)}
                          onChange={() => handleMultiSelect('recommendedSeason', season)}
                        />
                        <span>{season}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Scope Level</label>
                  <select
                    value={formData.scope.level}
                    onChange={(e) => handleScopeChange('level', e.target.value)}
                  >
                    <option value="Global">Global</option>
                    <option value="Country">Country</option>
                    <option value="State">State</option>
                  </select>
                </div>

                {/* Country dropdown - show when scope level is Country */}
                {formData.scope.level === 'Country' && (
                  <div className="form-group">
                    <label>Select Country *</label>
                    <select
                      value={formData.selectedCountry}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      className={errors.selectedCountry ? 'error' : ''}
                    >
                      <option value="">-- Select a Country --</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                    {errors.selectedCountry && <span className="error-message">{errors.selectedCountry}</span>}
                  </div>
                )}

                {/* State dropdown - show when scope level is State */}
                {formData.scope.level === 'State' && (
                  <div className="form-group">
                    <label>Select State *</label>
                    <select
                      value={formData.selectedState}
                      onChange={(e) => handleStateChange(e.target.value)}
                      className={errors.selectedState ? 'error' : ''}
                    >
                      <option value="">-- Select a State --</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    {errors.selectedState && <span className="error-message">{errors.selectedState}</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={createJourney}
                disabled={isSubmitting}
              >
                {isSubmitting 
                  ? (createdJourneyId ? 'Saving...' : 'Creating...') 
                  : (createdJourneyId ? 'Save Changes' : 'Create & Review')}
              </button>
            </div>
          </div>
        ) : (
          <div className="modal-content">
            <div className="review-content">
              <div className="review-form-section">
              <h3>Journey Details</h3>
              {editPolygonOnly && (
                <p className="review-edit-intro" style={{ marginBottom: '16px', fontSize: '14px', color: '#555' }}>
                  You can edit the journey name, description, visibility, and other properties below, or adjust the polygon boundaries on the map.
                </p>
              )}

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter journey title"
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <span className="error-message">{errors.title}</span>}
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter journey description"
                  rows={4}
                  className={errors.description ? 'error' : ''}
                />
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>

              <div className="form-group">
                <label>Visibility *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="public"
                      checked={formData.public === true || formData.public === 'true'}
                      onChange={() => handleInputChange('public', true)}
                    />
                    <span>Public</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="public"
                      checked={formData.public === false || formData.public === 'false'}
                      onChange={() => handleInputChange('public', false)}
                    />
                    <span>Private</span>
                  </label>
                </div>
              </div>

              <div className="review-badges">
                <span className="badge">{formData.public === true || formData.public === 'true' ? 'Public' : 'Private'}</span>
                <span className="badge">{formData.type}</span>
                {formData.scope.level && <span className="badge">{formData.scope.level}</span>}
              </div>
            </div>

            <div className="review-map">
              <h3>Edit Boundaries</h3>
              <p className="review-map-instruction" style={{ marginBottom: '12px', fontSize: '14px', color: '#555' }}>
                {editPolygonOnly ? (
                  <>Edit the journey name and description above if needed. <strong>Click a polygon</strong> to edit its location name. To adjust the polygon shape: click the <strong>pencil icon</strong> in the map toolbar (top-left), then click on the polygon to edit its vertices. When done, click <strong>Save Changes</strong> below.</>
                ) : (
                  <>Click the <strong>pencil icon</strong> in the map toolbar (top-left), then click on the polygon to edit its vertices. Drag vertices to adjust the shape. When done, click <strong>Save Changes</strong> below.</>
                )}
              </p>
              {formData.geojson && formData.geojson.features && formData.geojson.features.length > 0 ? (
                <MapEditor
                  key={`${createdJourneyId || 'new-journey'}-map`}
                  initialGeoJson={formData.geojson}
                  onGeoJsonChange={(geoJson) => {
                    setFormData(prev => ({ ...prev, geojson: geoJson }));
                  }}
                  height="450px"
                  showAdvanced={false}
                  editMode={true}
                  editExistingHint={!!createdJourneyId}
                />
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  {formData.geojson ? 'No features found in boundaries.' : 'No boundaries defined for this journey.'}
                  {formData.geojson && console.log('GeoJSON exists but no features:', formData.geojson)}
                </div>
              )}
            </div>

            {/* Optional Fields - moved below map */}
            <div className="review-form-section">
              <div className="form-section">
                <h3 
                  className="collapsible-header"
                  onClick={() => setIsOptionalInfoExpanded(!isOptionalInfoExpanded)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <i className={`fas fa-chevron-${isOptionalInfoExpanded ? 'down' : 'right'}`} style={{ fontSize: '12px' }}></i>
                  Optional Information
                </h3>
                
                {isOptionalInfoExpanded && (
                  <div className="optional-info-content">

                <div className="form-group">
                  <label>Desires</label>
                  <div className="checkbox-group">
                    {desireOptions.map(desire => (
                      <label key={desire} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.desires.includes(desire)}
                          onChange={() => handleMultiSelect('desires', desire)}
                        />
                        <span>{desire}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Feelings</label>
                  <div className="checkbox-group">
                    {feelingOptions.map(feeling => (
                      <label key={feeling} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.feelings.includes(feeling)}
                          onChange={() => handleMultiSelect('feelings', feeling)}
                        />
                        <span>{feeling}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Goals</label>
                  <div className="checkbox-group">
                    {goalOptions.map(goal => (
                      <label key={goal} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.goals.includes(goal)}
                          onChange={() => handleMultiSelect('goals', goal)}
                        />
                        <span>{goal}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Recommended Season</label>
                  <div className="checkbox-group">
                    {seasonOptions.map(season => (
                      <label key={season} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.recommendedSeason.includes(season)}
                          onChange={() => handleMultiSelect('recommendedSeason', season)}
                        />
                        <span>{season}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Scope Level</label>
                  <select
                    value={formData.scope.level}
                    onChange={(e) => handleScopeChange('level', e.target.value)}
                  >
                    <option value="Global">Global</option>
                    <option value="Country">Country</option>
                    <option value="State">State</option>
                  </select>
                </div>

                {/* Country dropdown - show when scope level is Country */}
                {formData.scope.level === 'Country' && (
                  <div className="form-group">
                    <label>Select Country *</label>
                    <select
                      value={formData.selectedCountry}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      className={errors.selectedCountry ? 'error' : ''}
                    >
                      <option value="">-- Select a Country --</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                    {errors.selectedCountry && <span className="error-message">{errors.selectedCountry}</span>}
                  </div>
                )}

                {/* State dropdown - show when scope level is State */}
                {formData.scope.level === 'State' && (
                  <div className="form-group">
                    <label>Select State *</label>
                    <select
                      value={formData.selectedState}
                      onChange={(e) => handleStateChange(e.target.value)}
                      className={errors.selectedState ? 'error' : ''}
                    >
                      <option value="">-- Select a State --</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    {errors.selectedState && <span className="error-message">{errors.selectedState}</span>}
                  </div>
                )}
                  </div>
                )}
              </div>
            </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={handleCancel}>
                {editPolygonOnly ? 'Cancel' : 'Cancel & Delete'}
              </button>
              <button className="btn-primary" onClick={handleAccept}>
                {editPolygonOnly ? 'Save Changes' : 'Accept & Activate'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateJourneyModal;

